import { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { ContentType, Res } from '../../../../_models/Game';
import { Props } from './types';
import DeviceManager from '../../../../components/util/deviceManager';
import FullScreen from '../../regular/fullScreen/FullScreen';
import { FullscreenButton } from '../../../../units';
import ClipScreen from '@/components/tour/game/ClipScreen';
import { getVideoLink } from '@/components/util/all';
import { AudioModule } from '../../../../components-new/common/AudioModule';

const audioHandler = AudioModule.getInstance();

const ConditionModal = ({ show, gameId, condition, onClose, initialDuplicates }: Props) => {
  const closeModalFunction = useCallback(() => {
    onClose();
  }, [onClose]);

  const pauseTourMusic = async () => {
    await audioHandler.stopTourMusic();
  };

  useEffect(() => {
    if (condition?.content_type === ContentType.Video) {
      setTimeout(pauseTourMusic, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  if (!show) return <div />;

  const isMobile = DeviceManager.isMobile();

  let contentLink;

  if (
    condition.content_type === ContentType.Picture ||
    condition.content_type === ContentType.Sound
  ) {
    contentLink = `${process.env.NEXT_PUBLIC_CDN}/games/${gameId}/conditions/${condition.id}/${condition.content_value}`;
  }

  let audioType;

  if (condition.content_type === ContentType.Sound) {
    const type = condition.content_value.replace(/.+[.]/g, '');
    if (type === 'ogg') audioType = 'audio/ogg';
    else if (type === 'wav') audioType = 'audio/wav';
    else audioType = 'audio/mpeg';
  }

  let styles = {};
  let height;
  let width;
  let widthIsAlmostFullscreen = false;
  const shouldConsiderHeightWidth =
    condition.content_type === ContentType.Video || condition.content_type === ContentType.Picture;
  if (shouldConsiderHeightWidth) {
    const resKey = isMobile ? 'resMobile' : 'res';
    const unit = condition[resKey] === Res.Pixels ? 'px' : 'vw';
    height = isMobile ? condition.heightMobile : condition.height;
    width = isMobile ? condition.widthMobile : condition.width;
    widthIsAlmostFullscreen = condition[resKey] === Res.Percentage && width > 95;
    styles = {
      height: `${height}${unit}`,
      width: `${width}${unit}`,
    };
  }

  const getCurrentLanguage = () => {
    const pathnameSplitted = window.location.pathname.split('/');
    const currentSlug = pathnameSplitted[pathnameSplitted.length - 1];
    return initialDuplicates.find((duplicate: any) => duplicate.slug === currentSlug);
  };

  return (
    <div className={classNames('game-condition-popup-modal')}>
      <div
        className={classNames(
          'game-condition-content-wrap',
          (condition.content_type === ContentType.Video ||
            condition.content_type === ContentType.Picture) &&
            'bigger',
          condition.content_type === ContentType.Text && 'narrow',
        )}
      >
        <i className="fas fa-times exppp" onClick={closeModalFunction} />
        {condition.content_type === ContentType.Text && condition.content_value}
        {condition.content_type === ContentType.Video && (
          <FullScreen
            attrs={{
              id: 'condition-video-fullscreen',
              styles: { background: 'transparent' },
            }}
            startFullscreen={condition.fullscreen}
          >
            {({ isFullScreen, toggleFullscreen }) => (
              <>
                <FullscreenButton
                  onClick={toggleFullscreen}
                  isFullscreen={isFullScreen || widthIsAlmostFullscreen}
                />
                {condition.fullscreen ? (
                  <ClipScreen
                    subtitles={getCurrentLanguage()?.languageCode}
                    videoUrl={condition.content_value}
                    close={closeModalFunction}
                    autoClose
                    disableAutoplay={!condition.autoPlay}
                  />
                ) : (
                  <iframe
                    src={getVideoLink(condition.content_value, {
                      autoplay: condition.autoPlay ? 1 : 0,
                      subtitles: getCurrentLanguage()?.languageCode,
                    })}
                    allow="autoplay"
                    allowFullScreen
                    style={isFullScreen ? {} : styles}
                  />
                )}
              </>
            )}
          </FullScreen>
        )}
        {condition.content_type === ContentType.Sound && (
          <audio controls>
            <source src={contentLink} type={audioType} />
            Your browser does not support the audio element.
          </audio>
        )}
        {condition.content_type === ContentType.Picture && (
          <img alt="popup" src={contentLink} style={styles} />
        )}
      </div>
    </div>
  );
};

export default ConditionModal;
